import { Icon, Icons } from '@/components/icon';
import { ContactAvatar } from '@/components/inbox/contact-avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo } from '@/services/api-allo/client';
import { Plus } from 'lucide-react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

type Contact = components['schemas']['Contact'];

export interface TeamMemberCellProps {
  members?: Array<{ id?: string; name?: string | null; last_name?: string | null }> | null;
  selectedMemberIds?: string[];
  onMembersChange?: (memberIds: string[]) => void;
  onCreateContact?: () => void;
  maxVisible?: number;
  readOnly?: boolean;
  className?: string;
  /** When provided, makes the popover controlled */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render prop that receives the display content and returns the trigger element */
  renderTrigger?: (displayContent: React.ReactNode) => React.ReactNode;
}

function getMemberName(member: { name?: string | null; last_name?: string | null }): string {
  const firstName = member.name ?? '';
  const lastName = member.last_name ?? '';
  return `${firstName} ${lastName}`.trim();
}

function getContactName(contact: Contact): string {
  const firstName = contact.name ?? '';
  const lastName = contact.last_name ?? '';
  return `${firstName} ${lastName}`.trim();
}

function ContactRow({
  name,
  isSelected,
  onClick,
}: {
  name: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full h-7 flex items-center justify-between px-2 rounded-md transition-colors cursor-pointer hover:bg-primary-3',
      )}
    >
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <ContactAvatar name={name} size="2xs" className="shrink-0" />
        <P3 className="text-primary-100 truncate">{name}</P3>
      </div>
      <Checkbox checked={isSelected} className="size-3 pointer-events-none" />
    </button>
  );
}

export const TeamMemberCell = memo(function TeamMemberCell({
  members,
  selectedMemberIds = [],
  onMembersChange,
  onCreateContact,
  maxVisible = 2,
  readOnly = false,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  renderTrigger,
}: TeamMemberCellProps) {
  const { t } = useTranslation('desktop_ui');
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  // Use the same POST filter endpoint as the companies table TeamPopover
  const { data, isLoading } = $apiAllo.useQuery(
    'post',
    '/v2/contact/filter',
    {
      body: {
        filter: {
          and: searchValue.trim()
            ? [{ field: 'name', operator: 'contains', value: searchValue.trim() }]
            : [],
        },
        sort: [{ field: 'last_activity_date', order: 'desc' }],
        pagination: { page: 0, size: 10 },
      },
    },
    {
      enabled: isOpen,
      staleTime: 5000,
    },
  );

  const contacts = useMemo(() => {
    return (data?.data as Contact[]) ?? [];
  }, [data?.data]);

  // Get selected contacts info from the contacts list or members prop
  const selectedContacts = useMemo(() => {
    const selected: Array<{ id: string; name: string }> = [];
    for (const id of selectedMemberIds) {
      // First check if it's in the fetched contacts
      const contact = contacts.find((c) => c.id === id);
      if (contact) {
        selected.push({ id, name: getContactName(contact) });
      } else {
        // Check in members prop
        const member = members?.find((m) => m.id === id);
        if (member) {
          selected.push({ id, name: getMemberName(member) });
        }
      }
    }
    return selected;
  }, [selectedMemberIds, contacts, members]);

  // Filter out already selected members from search results
  const filteredContacts = useMemo(
    () => contacts.filter((contact) => !selectedMemberIds.includes(contact.id ?? '')),
    [contacts, selectedMemberIds],
  );

  const visibleMembers = useMemo(() => {
    if (!members || members.length === 0) {
      return [];
    }
    return members.slice(0, maxVisible);
  }, [members, maxVisible]);

  const handleToggleMember = useCallback(
    (contactId: string) => {
      if (!onMembersChange) {
        return;
      }

      const isSelected = selectedMemberIds.includes(contactId);
      if (isSelected) {
        onMembersChange(selectedMemberIds.filter((id) => id !== contactId));
      } else {
        onMembersChange([...selectedMemberIds, contactId]);
      }
    },
    [selectedMemberIds, onMembersChange],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(open);
      } else {
        setUncontrolledOpen(open);
      }
      if (!open) {
        setSearchValue('');
      }
    },
    [isControlled, controlledOnOpenChange],
  );

  const displayContent = useMemo(() => {
    if (selectedMemberIds.length === 0 && (!members || members.length === 0)) {
      return (
        <div className="flex items-center gap-1 text-primary-40">
          <Plus className="size-3" />
          <P3>{t('components.companies.teamMember.addMember')}</P3>
        </div>
      );
    }

    // Use selectedMemberIds count if available, otherwise fall back to members
    const displayMembers = selectedMemberIds.length > 0 ? selectedContacts : visibleMembers;
    const count = selectedMemberIds.length > 0 ? selectedMemberIds.length : (members?.length ?? 0);
    const visibleCount = Math.min(count, maxVisible);
    const remaining = Math.max(0, count - maxVisible);

    return (
      <div className="flex items-center gap-1">
        <div className="flex -space-x-1">
          {displayMembers.slice(0, visibleCount).map((member, index) => {
            if (!member) {
              return null;
            }
            const name =
              'name' in member && typeof member.name === 'string'
                ? member.name
                : getMemberName(member as { name?: string | null; last_name?: string | null });
            return (
              <ContactAvatar
                key={member.id ?? index}
                name={name}
                size="xs"
                className={cn(index > 0 && 'border border-white rounded-full')}
              />
            );
          })}
        </div>
        {visibleCount === 1 && displayMembers[0] ? (
          <P3 className="truncate text-primary-60 underline decoration-dotted underline-offset-2">
            {'name' in displayMembers[0] && typeof displayMembers[0].name === 'string'
              ? displayMembers[0].name
              : getMemberName(
                  displayMembers[0] as { name?: string | null; last_name?: string | null },
                )}
          </P3>
        ) : remaining > 0 ? (
          <P3 className="text-primary-40">+{remaining}</P3>
        ) : null}
      </div>
    );
  }, [members, selectedMemberIds, selectedContacts, visibleMembers, maxVisible, t]);

  if (readOnly) {
    return <div className={cn('flex items-center', className)}>{displayContent}</div>;
  }

  const hasSelectedMembers = selectedMemberIds.length > 0;

  const triggerContent = renderTrigger ? (
    renderTrigger(displayContent)
  ) : (
    <button
      type="button"
      className={cn(
        'flex items-center rounded-sm transition-colors cursor-pointer',
        'focus:outline-none',
        className,
      )}
    >
      {displayContent}
    </button>
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl bg-white shadow-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Search input */}
        <div className="flex h-7 items-center gap-1 px-2 py-1">
          <Icon name={Icons.MagnifyingGlass} className="text-s text-primary-50 leading-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('components.companies.teamMember.searchContacts')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="flex-1 bg-transparent font-inter font-medium text-primary-100 text-s caret-highlight-default outline-none placeholder:text-primary-60"
          />
        </div>

        <div className="h-px w-full bg-primary-10" />

        {/* Contacts list */}
        <ScrollArea className="max-h-[160px]" variant="popover">
          <div className="flex flex-col gap-0.5 p-1">
            {/* Selected members section */}
            {hasSelectedMembers &&
              selectedContacts.map((member) => (
                <ContactRow
                  key={member.id}
                  name={member.name}
                  isSelected
                  onClick={() => handleToggleMember(member.id)}
                />
              ))}

            {/* Search results / All contacts section */}
            {isLoading ? (
              <div className="py-3 text-center">
                <P3 className="text-primary-40">{t('components.contacts.table.loading')}</P3>
              </div>
            ) : filteredContacts.length > 0 ? (
              <>
                {hasSelectedMembers && <div className="h-px w-full bg-primary-10 my-0.5" />}
                {filteredContacts.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    name={getContactName(contact)}
                    isSelected={false}
                    onClick={() => contact.id && handleToggleMember(contact.id)}
                  />
                ))}
              </>
            ) : !hasSelectedMembers ? (
              <div className="py-3 text-center">
                <P3 className="text-primary-40">{t('components.sidebar.noResults')}</P3>
              </div>
            ) : null}
          </div>
        </ScrollArea>

        {/* Create Contact button */}
        {onCreateContact && (
          <>
            <div className="h-px w-full bg-primary-10" />
            <button
              type="button"
              onClick={() => {
                onCreateContact();
                handleOpenChange(false);
              }}
              className="w-full flex items-center gap-1 px-2 py-1.5 transition-colors hover:bg-primary-3 cursor-pointer"
            >
              <Plus className="size-[10.667px] text-primary-80" />
              <P3 className="text-primary-90 font-medium">
                {t('components.companies.teamMember.createContact')}
              </P3>
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
});
